import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { MyMediaProvider, useMyMediaContext } from "./context/MyMediaContext";
import { SearchBar } from "./components/SearchBar";
import { FileGrid } from "./components/FileGrid";
import { AssignmentFooter } from "./components/AssignmentFooter";
import { ViewToggle } from "./components/ViewToggle";
import { SortDropdown } from "./components/SortDropdown";
import { FileTypeFilter } from "./components/FileTypeFilter";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EntityRecord } from "@/types/entity";
import { useMemo } from "react";
import { ENTITY_CONFIGS } from "@/other/constants/EntityManagerConfig";
import { FileCountDisplay } from "./components/FileCountDisplay";
import { UploadButton } from "./components/UploadButton";
import { Check, X } from "lucide-react";
import { Button } from "../../ui/button";

interface MyMediaPopupProps {
  record?: {
    entityRecord?: EntityRecord;
    entityType?: string;
    [key: string]: any;
  } | null;
  onClose: () => void;
  editorInstance?: {
    className?: string;
    [key: string]: unknown;
  };
}

const MyMediaPopupContent = ({ onClose, editorInstance }: { onClose: () => void; editorInstance?: MyMediaPopupProps["editorInstance"] }) => {
  const { files, selectedFiles, selectAllFiles, clearSelection, entityRecord } = useMyMediaContext();

  const allSelected = files.length > 0 && selectedFiles.size === files.length;
  const hasEntity = entityRecord !== null;

  return (
    <BaseModal
      record={{}}
      onClose={onClose}
      title="My Media"
      description="Manage all your media files"
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "80vw",
        height: "80vh",
        maxHeight: "100vh",
        maxWidth: "100vw",
        minWidth: "min(70rem, 100vw)",
        minHeight: "min(55rem, 100vh)",
      }}
      modal={false}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
    >
      <BaseModalBody>
        <div className="flex flex-col h-full">
          <div className="flex-shrink-0 pb-6 border-b border-border/50 mb-6 space-y-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center justify-between gap-4">
              <SearchBar />
            </div>
              <div className="flex items-center gap-3">
                <FileCountDisplay />
                <ViewToggle />
                <SortDropdown />
                <FileTypeFilter />
                {files.length > 0 && hasEntity && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={allSelected ? clearSelection : selectAllFiles}
                  >
                    {allSelected ? (
                      <>
                        <X className="h-4 w-4" />
                        Unselect all
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        Select all
                      </>
                    )}
                  </Button>
                )}
                <UploadButton />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto min-h-0">
            <FileGrid />
          </div>
        </div>
      </BaseModalBody>
      <AssignmentFooter />
    </BaseModal>
  );
};

export const MyMediaPopup = ({ record, onClose, editorInstance }: MyMediaPopupProps) => {
  const { entityManager, project } = useSwivuBryntumContext();
  const { entityRecord: paramEntityRecord, entityType: paramEntityType } = record || {};

  /* We have to calculate & pass entity record */
  const entityRecord = useMemo(() => {
    if (paramEntityRecord) {
      return paramEntityRecord;
    }

    if (entityManager?.isOpen && entityManager?.entityType && entityManager?.entityId) {
      const entityType = entityManager.entityType;
      const entityId = entityManager.entityId;
      const config = ENTITY_CONFIGS[entityType];
      if (config) {
        const entity = project.getCrudStore(config.storeKey as string).getById(Number(entityId));
        if (entity) {
          return entity as unknown as EntityRecord;
        }
      }
    }

    return null;
  }, [paramEntityRecord, paramEntityType, entityManager?.isOpen, project]);

  return (
    <MyMediaProvider initialEntityRecord={entityRecord}>
      <MyMediaPopupContent onClose={onClose} editorInstance={editorInstance} />
    </MyMediaProvider>
  );
};
